/**
 * Human-readable drop labels for multi-country bulk edit (list name + country).
 */

import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import { mergeBulkEditDraftIds } from './bulkEditAffectedIds';

export type LeadsListsByCountry = Record<string, Array<{ id: number; name: string }>>;

function listNameForDrop(d: PreparedDrop, leadsListsByCountry?: LeadsListsByCountry): string | undefined {
  const lists = leadsListsByCountry?.[d.countryKey];
  return lists?.find((l) => l.id === d.leadsListWiserId)?.name?.trim();
}

/** Label: country · leads list name when known, else title, else campaign. */
export function labelPreparedDropForBulkEdit(
  d: PreparedDrop,
  indexWithinCountry: number,
  leadsListsByCountry?: LeadsListsByCountry,
): string {
  const listName = listNameForDrop(d, leadsListsByCountry);
  const name = listName || d.titleBase?.trim() || d.campaignName?.trim();
  if (name) return `${d.countryKey} · ${name}`;
  return `${d.countryKey} · Drop ${indexWithinCountry + 1}`;
}

export function buildBulkEditDropLabelMapsFromOrdered(
  orderedIds: string[],
  mergedDrops: PreparedDrop[],
  leadsListsByCountry?: LeadsListsByCountry,
): { flat: DropLabel[]; byCountry: Record<string, DropLabel[]> } {
  const byId = new Map(mergedDrops.map((dr) => [dr.id, dr]));
  const counter = new Map<string, number>();
  const byCountry: Record<string, DropLabel[]> = {};
  const flat: DropLabel[] = [];
  for (const id of orderedIds) {
    const d = byId.get(id);
    if (!d) continue;
    const n = (counter.get(d.countryKey) ?? 0) + 1;
    counter.set(d.countryKey, n);
    const entry: DropLabel = { id, label: labelPreparedDropForBulkEdit(d, n - 1, leadsListsByCountry) };
    flat.push(entry);
    const list = byCountry[d.countryKey] ?? [];
    list.push(entry);
    byCountry[d.countryKey] = list;
  }
  return { flat, byCountry };
}

export function buildBulkEditDropLabelMaps(
  selectedIds: string[],
  propagateToCountries: string[],
  mergedDrops: PreparedDrop[],
  leadsListsByCountry?: LeadsListsByCountry,
): { flat: DropLabel[]; byCountry: Record<string, DropLabel[]> } {
  return buildBulkEditDropLabelMapsFromOrdered(
    mergeBulkEditDraftIds(selectedIds, propagateToCountries, mergedDrops),
    mergedDrops,
    leadsListsByCountry,
  );
}
